import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createUseStyles } from 'react-jss';

interface IWrapperProps {
  wrapper: boolean;
  isWrapperOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  parentElement: HTMLElement; // Parent element for positioning reference
}

const Wrapper = ({ wrapper, isWrapperOpen, onClose, children, parentElement }: IWrapperProps) => {
  const useStyles = createUseStyles({
    overlayContainer: {
      position: 'absolute',
      zIndex: 1000,
      backgroundColor: '#212121',
      border: '0px',
      borderRight: '2px solid #3c3c3c',
      padding: '24px',
      borderRadius: '0px',
      boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
      display: 'block',
    },
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 999,
      display: 'block',
    },
  });

  const classes = useStyles();
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [positionCalculated, setPositionCalculated] = useState(false);
  const [overlayHeight, setOverlayHeight] = useState(250);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (parentElement && isWrapperOpen) {
      const rect = parentElement.getBoundingClientRect();
      const wrapperHeight = overlayHeight || 250;
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - rect.bottom;

      const viewportTop = window.scrollY;
      const viewportBottom = viewportTop + viewportHeight;
      let newTop = rect.bottom + window.scrollY + 20;
      const overflowBottom = newTop + wrapperHeight + 20 - viewportBottom;
      if (overflowBottom > 0) {
        newTop -= overflowBottom;
      }
      const overflowTop = viewportTop + 20 - newTop;
      if (overflowTop > 0) {
        newTop += overflowTop;
      }

      setPosition({
        top: newTop - 42,
        left: rect.left + window.scrollX - 318,
      });
      setPositionCalculated(true);
    }
  }, [parentElement, isWrapperOpen, overlayHeight]);

  useLayoutEffect(() => {
    if (overlayRef.current && isWrapperOpen) {
      setOverlayHeight(overlayRef.current.offsetHeight);
    }
  }, [isWrapperOpen, children]);

  const handleClose = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (wrapper && isWrapperOpen && positionCalculated) {
    return createPortal(
      <>
        <div className={classes.backdrop} onClick={handleClose} />
        <div ref={overlayRef} className={classes.overlayContainer} style={{ top: position.top, left: position.left }}>
          {children}
        </div>
      </>,
      document.body, // Render at the root level
    );
  }

  return null;
};

export default Wrapper;
